import { liveQuery } from "dexie";
import { useEffect, useState } from "react";
import { api, FalhaApi } from "../api";
import {
  offlineDb,
  type MutacaoAchadoAvulso,
  type MutacaoCampo,
  type MutacaoFoto,
  type MutacaoObservacao,
  type MutacaoPendente,
  type MutacaoResposta,
} from "./db";

/**
 * Motor de drenagem da fila offline (Fase 6). Cada mutação é uma chamada de API independente
 * (não um diff encadeado), então uma falha permanente numa não trava as outras — só falhas de
 * rede param a drenagem inteira (assume-se offline, tenta de novo no próximo gatilho).
 */

const MAX_TENTATIVAS = 3;

let sincronizandoAgora = false;
const ouvintesEstado = new Set<() => void>();
function notificarOuvintes() {
  ouvintesEstado.forEach((fn) => fn());
}

// Barramento simples pra quem enfileirou uma mutação (ex.: VistoriaDetalhe.tsx, mostrando um
// placeholder de foto "pendente") saber quando ela foi de fato aplicada ou definitivamente
// descartada, sem o motor de sincronização precisar conhecer nada de React/estado de tela.
export const eventosSincronizacao = new EventTarget();

export interface MutacaoAplicadaDetalhe {
  mutationId: string;
  vistoriaId: string;
  tipo: MutacaoPendente["tipo"];
  resultado?: unknown;
}

export interface MutacaoDescartadaDetalhe {
  mutationId: string;
  vistoriaId: string;
  tipo: MutacaoPendente["tipo"];
  erro: string;
}

async function enfileirar<T extends MutacaoPendente>(
  tipo: T["tipo"],
  vistoriaId: string,
  payload: T["payload"]
): Promise<string> {
  const mutationId = crypto.randomUUID();
  const mutacao = {
    id: mutationId,
    vistoriaId,
    tipo,
    payload,
    criadoEm: new Date().toISOString(),
    tentativas: 0,
  } as MutacaoPendente;
  await offlineDb.mutacoesPendentes.add(mutacao);
  void drenar();
  return mutationId;
}

export const enfileirarResposta = (vistoriaId: string, payload: MutacaoResposta["payload"]) =>
  enfileirar<MutacaoResposta>("resposta", vistoriaId, payload);

export const enfileirarObservacao = (vistoriaId: string, payload: MutacaoObservacao["payload"]) =>
  enfileirar<MutacaoObservacao>("observacao", vistoriaId, payload);

export const enfileirarAchadoAvulso = (vistoriaId: string, payload: MutacaoAchadoAvulso["payload"]) =>
  enfileirar<MutacaoAchadoAvulso>("achado_avulso", vistoriaId, payload);

export const enfileirarCampo = (vistoriaId: string, payload: MutacaoCampo["payload"]) =>
  enfileirar<MutacaoCampo>("campo", vistoriaId, payload);

export const enfileirarFoto = (vistoriaId: string, payload: MutacaoFoto["payload"]) =>
  enfileirar<MutacaoFoto>("foto", vistoriaId, payload);

async function aplicar(mutacao: MutacaoPendente): Promise<unknown> {
  switch (mutacao.tipo) {
    case "resposta":
      return api.salvarResposta(mutacao.vistoriaId, mutacao.payload);
    case "observacao":
      return api.salvarObservacao(mutacao.vistoriaId, mutacao.payload);
    case "achado_avulso":
      return api.salvarAchadoAvulso(mutacao.vistoriaId, mutacao.payload);
    case "campo":
      return api.editarVistoria(mutacao.vistoriaId, mutacao.payload);
    case "foto": {
      const { arquivo, nomeArquivo, ...dados } = mutacao.payload;
      return api.uploadFoto(mutacao.vistoriaId, arquivo, nomeArquivo, dados, mutacao.id);
    }
  }
}

/**
 * Percorre a fila em ordem de criação. Para cada item: sucesso remove da fila e avisa
 * `eventosSincronizacao`; falha de rede (não chegou a falar com o servidor) para a drenagem
 * inteira ali — não adianta insistir nos próximos, provavelmente estão todos offline também;
 * falha da aplicação (servidor respondeu recusando, ex.: vistoria não está mais em andamento) não
 * tem conserto por retentativa, mas não bloqueia os outros itens — some da fila depois de
 * MAX_TENTATIVAS, e antes disso fica pra tentar de novo no próximo ciclo (cobre erros 5xx
 * passageiros).
 */
export async function drenar(): Promise<void> {
  if (sincronizandoAgora || !navigator.onLine) return;
  sincronizandoAgora = true;
  notificarOuvintes();

  try {
    const pendentes = await offlineDb.mutacoesPendentes.orderBy("criadoEm").toArray();
    for (const mutacao of pendentes) {
      try {
        const resultado = await aplicar(mutacao);
        await offlineDb.mutacoesPendentes.delete(mutacao.id);
        eventosSincronizacao.dispatchEvent(
          new CustomEvent<MutacaoAplicadaDetalhe>("aplicada", {
            detail: {
              mutationId: mutacao.id,
              vistoriaId: mutacao.vistoriaId,
              tipo: mutacao.tipo,
              resultado,
            },
          })
        );
      } catch (e) {
        if (!(e instanceof FalhaApi) || e.corpoInvalido) {
          // Falha de rede de verdade (fetch nem completou) OU o servidor respondeu mas não com
          // JSON reconhecível (502/503 de um proxy com o backend fora do ar, ex.: Vite em dev ou
          // Traefik em produção) — nos dois casos a mutação NUNCA chegou a ser avaliada pela
          // aplicação, então não é justo contar como tentativa nem arriscar descartar depois de
          // 3 falhas contra um servidor que nunca respondeu de verdade. Achado testando de
          // verdade (servidor derrubado de propósito): sem essa distinção, a fila descartava
          // dados reais do usuário em segundos.
          break;
        }
        const tentativas = mutacao.tentativas + 1;
        if (tentativas >= MAX_TENTATIVAS) {
          await offlineDb.mutacoesPendentes.delete(mutacao.id);
          eventosSincronizacao.dispatchEvent(
            new CustomEvent<MutacaoDescartadaDetalhe>("descartada", {
              detail: {
                mutationId: mutacao.id,
                vistoriaId: mutacao.vistoriaId,
                tipo: mutacao.tipo,
                erro: e.message,
              },
            })
          );
        } else {
          await offlineDb.mutacoesPendentes.update(mutacao.id, { tentativas, ultimoErro: e.message });
        }
        // Continua pros próximos itens — esse aqui não é problema de rede, os outros podem ter
        // mais sorte (ou pelo menos merecem a chance de tentar).
      }
    }
  } finally {
    sincronizandoAgora = false;
    notificarOuvintes();
  }
}

let automaticaIniciada = false;

/** Chamado uma vez em main.tsx. Gatilhos: evento online, aba voltando a ficar visível, polling
 * leve de 30s (cobre o caso de a conexão voltar sem o browser disparar o evento `online`, comum
 * em Wi-Fi de sinal fraco oscilando) e uma tentativa imediata ao iniciar, pra drenar pendências
 * deixadas de uma sessão anterior assim que o app abre com rede disponível. */
export function iniciarSincronizacaoAutomatica(): void {
  if (automaticaIniciada) return;
  automaticaIniciada = true;
  window.addEventListener("online", () => void drenar());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void drenar();
  });
  setInterval(() => void drenar(), 30_000);
  void drenar();
}

export interface EstadoSincronizacao {
  pendentes: number;
  online: boolean;
  sincronizando: boolean;
}

/** Hook de UI: contagem de pendências (reativa via Dexie liveQuery), online/offline do
 * `navigator`, e se uma drenagem está rolando agora — pra mostrar um indicador e um botão
 * "Sincronizar agora" (ver componentes/IndicadorSincronizacao.tsx). */
export function useSincronizacao(): EstadoSincronizacao & { sincronizarAgora: () => void } {
  const [pendentes, setPendentes] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  const [sincronizando, setSincronizando] = useState(sincronizandoAgora);

  useEffect(() => {
    const assinatura = liveQuery(() => offlineDb.mutacoesPendentes.count()).subscribe({
      next: setPendentes,
    });
    const aoFicarOnline = () => {
      setOnline(true);
      void drenar();
    };
    const aoFicarOffline = () => setOnline(false);
    window.addEventListener("online", aoFicarOnline);
    window.addEventListener("offline", aoFicarOffline);

    const ouvinte = () => setSincronizando(sincronizandoAgora);
    ouvintesEstado.add(ouvinte);

    return () => {
      assinatura.unsubscribe();
      window.removeEventListener("online", aoFicarOnline);
      window.removeEventListener("offline", aoFicarOffline);
      ouvintesEstado.delete(ouvinte);
    };
  }, []);

  return { pendentes, online, sincronizando, sincronizarAgora: () => void drenar() };
}
